import React, { useRef, useState } from 'react';
import { Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useChipFit } from '@/hooks/useChipFit';
import { BrokerGroupChip } from '../../shared/chips/BrokerGroupChip';

// One line of small chips plus the cell's vertical padding
const COLLAPSED_HEIGHT = 40;

/**
 * A user's other broker groups (brokerGroupIds, without the primary) as chips. Collapsed,
 * it shows one line: the chips that fit and "+x" for the rest, so the row's height comes
 * from the other cells. The arrow expands it to every chip, wrapped, and collapses it
 * again, as the feature flag targeting cells do.
 */
export const UserBrokerGroupsCell = ({ brokerGroupIds }) => {
	const chipsRef = useRef();
	const [expanded, setExpanded] = useState(false);
	// Every chip is shown from the start of expanding until collapsing has finished, so the
	// chips stay in place while the height animates
	const [showAll, setShowAll] = useState(false);

	const fitCount = useChipFit(chipsRef, brokerGroupIds.length);
	const hiddenCount = showAll ? 0 : Math.max(brokerGroupIds.length - fitCount, 0);
	const shownIds = showAll ? brokerGroupIds : brokerGroupIds.slice(0, fitCount);

	const handleToggle = (event) => {
		// Expanding or collapsing shouldn't also click the row
		event.stopPropagation();
		if (!expanded) {
			setShowAll(true);
		}
		setExpanded(!expanded);
	};

	return (
		<Stack direction='row' alignItems='center' spacing={0.5} sx={{ width: '100%', height: '100%' }}>
			<Collapse
				in={expanded}
				collapsedSize={COLLAPSED_HEIGHT}
				onExited={() => setShowAll(false)}
				sx={{ flex: 1, minWidth: 0 }}
			>
				<Stack
					ref={chipsRef}
					direction='row'
					flexWrap={showAll ? 'wrap' : 'nowrap'}
					gap={0.5}
					alignItems='center'
					sx={{ minWidth: 0, minHeight: COLLAPSED_HEIGHT, overflow: 'hidden', py: 1 }}
				>
					{shownIds.map((id) => (
						<BrokerGroupChip key={id} id={id} />
					))}
					{hiddenCount > 0 && (
						<Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
							{`+${hiddenCount}`}
						</Typography>
					)}
				</Stack>
			</Collapse>
			{(showAll || hiddenCount > 0) && (
				<Tooltip title={expanded ? 'Show less' : 'Show all broker groups'}>
					<IconButton
						size='small'
						onClick={handleToggle}
						aria-label={expanded ? 'Show fewer broker groups' : 'Show all broker groups'}
						aria-expanded={expanded}
					>
						{expanded ? (
							<KeyboardArrowUpIcon fontSize='small' />
						) : (
							<KeyboardArrowDownIcon fontSize='small' />
						)}
					</IconButton>
				</Tooltip>
			)}
		</Stack>
	);
};
